// Pitch detection via the YIN algorithm.
//
// YIN (de Cheveigné & Kawahara 2002) computes a cumulative-mean-
// normalised difference function whose first dip below a threshold
// gives the fundamental period. It's substantially more reliable than
// raw autocorrelation for musical content because it explicitly hunts
// the *fundamental*, not whichever harmonic happens to have the most
// energy. ~150 LOC, no FFT.
//
// Steps per analysis frame:
//   1. Difference function: d(τ) = Σ (x[j] − x[j+τ])²  for j = 0..W.
//   2. Cumulative mean normalised difference: d'(τ) = d(τ) /
//      ((1/τ) · Σ_{k=1..τ} d(k)). d'(0) = 1 by convention.
//   3. Find the first local minimum where d' < threshold. If none, the
//      frame isn't strongly tonal — skip it.
//   4. Refine the minimum's τ via parabolic interpolation against
//      neighbours, then f0 = sampleRate / refined_τ.
//
// For a buffer with many frames, we run YIN on each, drop atonal frames,
// and return the *median* Hz of the tonal ones — robust to a single
// noisy frame swinging the result, and honest about samples whose pitch
// drifts (those will have a wide spread + lower confidence).

const DEFAULT_THRESHOLD = 0.1;
const FRAME_SIZE = 2048;
const HOP_SIZE = 1024;
const MIN_HZ = 50;
const MAX_HZ = 2000;
// Per-frame YIN is O(W × searchRange) ≈ 3.5M ops at default settings.
// A 60-second source at 50% overlap = ~5000 frames × 3.5M = ~17 billion
// ops. We cap analysis to the first ~5 seconds of source — sustained
// tonal content doesn't need every frame to determine its fundamental,
// and onset transients near the start are usually atonal anyway.
const DEFAULT_MAX_ANALYSIS_SECONDS = 5;

export type PitchDetectOptions = {
  /** YIN threshold. Lower = pickier. Default 0.1 — the paper's value. */
  threshold?: number;
  /** Frame size in samples. Default 2048 — gives ~22 Hz resolution at 44.1k. */
  frameSize?: number;
  /** Cap analysis to the first N seconds of source. Default 5. Set to
   *  Infinity to analyse the full buffer (slow on long samples). */
  maxAnalysisSeconds?: number;
};

export type PitchEstimate = {
  hz: number;
  /** Fraction of analysis frames that found a tonal pitch, in [0, 1].
   *  A clean sustained note reads ~0.9+; a noisy / percussive sample
   *  reads near 0. */
  confidence: number;
};

export function detectPitch(
  channel: Float32Array,
  sampleRate: number,
  opts: PitchDetectOptions = {},
): PitchEstimate | null {
  const threshold = opts.threshold ?? DEFAULT_THRESHOLD;
  const frameSize = opts.frameSize ?? FRAME_SIZE;
  const maxAnalysisSeconds =
    opts.maxAnalysisSeconds ?? DEFAULT_MAX_ANALYSIS_SECONDS;
  const halfFrame = Math.floor(frameSize / 2);
  if (channel.length < frameSize) return null;

  // YIN search bounds in lag-units: lag = sampleRate / hz.
  const minLag = Math.max(2, Math.floor(sampleRate / MAX_HZ));
  const maxLag = Math.min(halfFrame, Math.ceil(sampleRate / MIN_HZ));
  if (minLag >= maxLag) return null;

  const analysisLength = Math.min(
    channel.length,
    Math.round(maxAnalysisSeconds * sampleRate),
  );
  const frameCount = Math.floor((analysisLength - frameSize) / HOP_SIZE) + 1;
  const hzPerFrame: number[] = [];
  let analysed = 0;

  for (let f = 0; f < frameCount; f++) {
    const start = f * HOP_SIZE;
    const tau = yinFrame(channel, start, frameSize, minLag, maxLag, threshold);
    analysed++;
    if (tau === null) continue;
    const hz = sampleRate / tau;
    // Final guard against numerical fluke.
    if (hz < MIN_HZ || hz > MAX_HZ) continue;
    hzPerFrame.push(hz);
  }

  if (hzPerFrame.length === 0 || analysed === 0) return null;

  // Median over tonal frames is robust against the occasional outlier
  // that single-best-frame strategies pick up.
  hzPerFrame.sort((a, b) => a - b);
  const mid = Math.floor(hzPerFrame.length / 2);
  const hz =
    hzPerFrame.length % 2 === 0
      ? (hzPerFrame[mid - 1]! + hzPerFrame[mid]!) / 2
      : hzPerFrame[mid]!;
  const confidence = hzPerFrame.length / analysed;
  return { hz, confidence };
}

// One YIN pass over a single frame. Returns the refined lag (a
// fractional number of samples), or null if no dip below the threshold
// was found.
function yinFrame(
  channel: Float32Array,
  start: number,
  frameSize: number,
  minLag: number,
  maxLag: number,
  threshold: number,
): number | null {
  // Step 1: difference function. Compute over τ ∈ [1, maxLag], not just
  // the search range — the cumulative-mean denominator in step 2 needs
  // every prior d(τ) including the ones we won't use as candidates.
  // Skipping τ < minLag here was a real bug: it left d[1..minLag-1] as
  // zeros, undersized the running sum, and over-inflated d'(τ) near
  // minLag (i.e. high frequencies under-detected).
  const d = new Float32Array(maxLag + 1);
  for (let tau = 1; tau <= maxLag; tau++) {
    let sum = 0;
    const upto = frameSize - tau;
    for (let j = 0; j < upto; j++) {
      const a = channel[start + j] ?? 0;
      const b = channel[start + j + tau] ?? 0;
      const diff = a - b;
      sum += diff * diff;
    }
    d[tau] = sum;
  }

  // Step 2: cumulative-mean-normalised difference.
  // d'(τ) = d(τ) / ((1/τ) · Σ_{k=1..τ} d(k)) = d(τ) · τ / Σ_{k=1..τ} d(k).
  const dPrime = new Float32Array(maxLag + 1);
  dPrime[0] = 1;
  let runningSum = 0;
  for (let tau = 1; tau <= maxLag; tau++) {
    runningSum += d[tau]!;
    if (tau < minLag) {
      dPrime[tau] = 1; // outside search range — never a candidate
      continue;
    }
    if (runningSum === 0) {
      dPrime[tau] = 1;
      continue;
    }
    dPrime[tau] = (d[tau]! * tau) / runningSum;
  }

  // Step 3: find the first local minimum that drops below threshold.
  // YIN's "absolute threshold" rule — first descent past the bar.
  let chosenTau = -1;
  for (let tau = minLag; tau <= maxLag; tau++) {
    if (dPrime[tau]! < threshold) {
      // Walk down to the local minimum.
      while (tau + 1 <= maxLag && dPrime[tau + 1]! < dPrime[tau]!) tau++;
      chosenTau = tau;
      break;
    }
  }
  if (chosenTau < 0) return null;

  // Step 4: parabolic interpolation around dPrime[chosenTau].
  return parabolicMin(dPrime, chosenTau);
}

function parabolicMin(values: Float32Array, idx: number): number {
  if (idx <= 0 || idx >= values.length - 1) return idx;
  const yPrev = values[idx - 1]!;
  const yCur = values[idx]!;
  const yNext = values[idx + 1]!;
  const denom = yPrev - 2 * yCur + yNext;
  if (Math.abs(denom) < 1e-12) return idx;
  const offset = (0.5 * (yPrev - yNext)) / denom;
  if (offset < -1 || offset > 1) return idx;
  return idx + offset;
}

// Convenience: Hz → MIDI note number (with cents offset folded in via
// fractional return). Used by callers that want to display "A4" instead
// of "440 Hz". Returns NaN for non-positive input — louder than silently
// returning MIDI 0 ("C-1"), which would let bugs propagate.
export function hzToMidi(hz: number): number {
  if (!Number.isFinite(hz) || hz <= 0) return NaN;
  return 69 + 12 * Math.log2(hz / 440);
}

// MIDI note number → "C4" / "F#3" style label. Returns "—" for NaN.
export function midiToNoteName(midi: number): string {
  if (!Number.isFinite(midi)) return '—';
  const NAMES = ['C', 'C#', 'D', 'D#', 'E', 'F', 'F#', 'G', 'G#', 'A', 'A#', 'B'];
  const rounded = Math.round(midi);
  const octave = Math.floor(rounded / 12) - 1;
  const name = NAMES[((rounded % 12) + 12) % 12]!;
  return `${name}${octave}`;
}
